import { useEffect, useState } from "react";

const API = process.env.REACT_APP_BACKEND_URL;

/**
 * Replaces the static hero stats bar with live numbers pulled from
 * /api/aggregate-stats (Crossref-derived: total DOIs, total citations).
 */
export default function LiveStats() {
  const [stats, setStats] = useState(null);
  const [animatedWorks, setAnimatedWorks] = useState(0);
  const [animatedCites, setAnimatedCites] = useState(0);

  useEffect(() => {
    let cancelled = false;
    fetch(`${API}/api/aggregate-stats`)
      .then((r) => (r.ok ? r.json() : null))
      .then((d) => !cancelled && d && setStats(d))
      .catch(() => {});
    return () => {
      cancelled = true;
    };
  }, []);

  // Animate counters on data arrival
  useEffect(() => {
    if (!stats) return;
    const target1 = stats.total_works;
    const target2 = stats.total_citations;
    const duration = 1400;
    const start = performance.now();
    let raf;
    const tick = (t) => {
      const p = Math.min(1, (t - start) / duration);
      const eased = 1 - Math.pow(1 - p, 3);
      setAnimatedWorks(Math.round(target1 * eased));
      setAnimatedCites(Math.round(target2 * eased));
      if (p < 1) raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [stats]);

  const cells = stats
    ? [
        { label: "DOI · Published", value: animatedWorks.toLocaleString(), sub: "Across both journals" },
        { label: "Citations", value: animatedCites.toLocaleString(), sub: "Crossref-indexed" },
        { label: "Journals", value: stats.journals_count.toString().padStart(2, "0"), sub: "Open Access" },
        { label: "Founded", value: stats.founded_year, sub: "Sofia · Bulgaria" },
      ]
    : [
        { label: "DOI · Published", value: "—", sub: "Loading from Crossref" },
        { label: "Citations", value: "—", sub: "Loading from Crossref" },
        { label: "Journals", value: "02", sub: "Open Access" },
        { label: "Founded", value: "2018", sub: "Sofia · Bulgaria" },
      ];

  return (
    <div
      data-testid="hero-stats-bar"
      className="bg-white/85 backdrop-blur-xl border border-[var(--sci-border)] shadow-[0_8px_40px_-12px_rgba(11,22,44,0.18)]"
    >
      <div className="grid grid-cols-2 md:grid-cols-4 divide-x divide-[var(--sci-border)]">
        {cells.map((s, i) => (
          <div
            key={s.label}
            data-testid={`hero-stat-${i}`}
            className="px-6 py-7 md:px-8 md:py-9 group cursor-default"
          >
            <div className="overline text-[var(--sci-text-muted)] mb-3">
              {s.label}
            </div>
            <div className="font-serif-display text-[var(--sci-navy)] text-4xl md:text-5xl leading-none group-hover:text-[var(--sci-teal)] transition-colors font-mono-sci-not">
              {s.value}
            </div>
            <div className="text-xs md:text-sm text-[var(--sci-text-muted)] mt-3 leading-snug flex items-center gap-2">
              {stats && (
                <span className="w-1.5 h-1.5 bg-[var(--sci-teal)] sci-pulse-dot rounded-full inline-block" />
              )}
              {s.sub}
            </div>
          </div>
        ))}
      </div>
      <div className="px-6 md:px-8 py-3 border-t border-[var(--sci-border)] flex items-center justify-between text-[10px] font-mono-sci tracking-wider text-[var(--sci-text-muted)]">
        <span>
          {stats ? "LIVE · CROSSREF API" : "FETCHING LIVE STATS…"}
        </span>
        <span className="hidden md:inline">
          UPDATED EVERY 30 MIN · CACHED SERVER-SIDE
        </span>
      </div>
    </div>
  );
}
